/**
 * Tiny fetch wrapper.
 *
 * - Always sends credentials (cookies).
 * - On 401, dispatches a custom event so the auth hook can redirect.
 * - Throws an ApiError with a `status` field — handy for differentiating
 *   401, 409 (conflict), 413 (too large), 429 (rate limited), etc.
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

const UNAUTH_EVENT = 'hh:unauthorized';

export function onUnauthorized(handler: () => void): () => void {
  const listener = () => handler();
  window.addEventListener(UNAUTH_EVENT, listener);
  return () => window.removeEventListener(UNAUTH_EVENT, listener);
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'accept': 'application/json',
      ...(init.body && !(init.body instanceof FormData)
        ? { 'content-type': 'application/json' }
        : {}),
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent(UNAUTH_EVENT));
  }

  let body: unknown = null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    body = await res.json().catch(() => null);
  }
  if (!res.ok) {
    const msg =
      (body as { error?: string } | null)?.error ??
      `request failed (${res.status})`;
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}

/** Convenience for uploads where we need to attach raw bytes. */
export async function apiUpload<T>(
  path: string,
  blob: Blob,
  filename: string,
  contentType: string,
): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': contentType,
      'x-filename': filename,
    },
    body: blob,
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent(UNAUTH_EVENT));
  }
  let body: unknown = null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    body = await res.json().catch(() => null);
  }
  if (!res.ok) {
    const msg =
      (body as { error?: string } | null)?.error ??
      `upload failed (${res.status})`;
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}
